import React, { useEffect, useMemo } from 'react';
import {
    ConnectionsIcon,
    ListItemButtonContent,
    StyledItemText,
    StyledList,
    StyledListItem,
    StyledLink,
} from '@/components/Hypertracker/HypertrackerSidebar/HypertrackerSidebarStyled';
import { Box, Stack } from '@mui/system';
import { Badge, Divider, Typography } from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { selectConnections } from '@/state/hypertracker/hypertrackerSelector';
import { toggleSpinner } from '@/state/general/generalSlice';
import { getConnections } from '@/api/hypertrackerApi';
import { setConnections, setModalWindow } from '@/state/hypertracker/hypertackerSlice';
import useIsMemberAuthorized from '@/hooks/useIsMemberAuthorized';
import { nFormatter, shortenString } from '@/lib/helpers/helpers';
import { matchPath, useLocation, useNavigate } from 'react-router';
import { ROUTES } from '@/lib/constants';
import { useTranslation } from 'react-i18next';
import { MODAL_TYPE_HANDLER } from '@/lib/types/hypertrackerTypes';
import { AUTH_TYPE } from '@/lib/types/generalTypes';
import { lighten } from '@mui/system/colorManipulator';

const ConnectionsList = ({ open = true, setOpen }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'hypertracker_sidebar' });
    const connections = useSelector((state: RootState) => selectConnections(state));
    const dispatch = useDispatch();
    const isMemberAuthorized = useIsMemberAuthorized();

    const onAddWalletClick = () => {
        dispatch(setModalWindow({ modalType: MODAL_TYPE_HANDLER.IS_OPEN_CONNECT_MODAL }));
    };
    const navigate = useNavigate();
    const location = useLocation();

    const fetchConnections = async () => {
        try {
            dispatch(toggleSpinner(true));
            const { data } = await getConnections();
            dispatch(setConnections(data));
        } catch (error) {
            console.error('Error fetching connections:', error);
        } finally {
            dispatch(toggleSpinner(false));
        }
    };

    const onNotAuthorizedClick = () => {
        dispatch(
            setModalWindow({
                modalType: MODAL_TYPE_HANDLER.IS_AUTH_MODAL,
                authorizationModalType: AUTH_TYPE.SIGN_IN,
            }),
        );
    };

    useEffect(() => {
        if (!isMemberAuthorized) return;
        fetchConnections();
    }, [isMemberAuthorized]);

    const list = useMemo(() => {
        if (!isMemberAuthorized) return [];
        const filtered = connections
            .filter((connection) => !connection.vault)
            .map((connection) => {
                let label = shortenString(connection.address);
                if (connection?.displayName || connection?.emoji) {
                    label = `${connection?.emoji || ''} ${connection?.displayName ? connection.displayName : ''}`;
                }

                return {
                    label,
                    value: connection?.totalEquity,
                    withBadge: Boolean(connection?.displayName || connection?.emoji),
                    address: connection.address,
                };
            })
            .toSorted((a, b) => b.value - a.value);
        if (filtered.length > 5) {
            return filtered.slice(0, 5);
        }
        return filtered;
    }, [connections, isMemberAuthorized]);

    const totalLength = useMemo(() => {
        if (!isMemberAuthorized) return 0;
        return connections.filter((connection) => !connection.vault).length;
    }, [connections, isMemberAuthorized]);

    const onConnectionsClick = () => {
        if (!isMemberAuthorized) {
            onNotAuthorizedClick();
            setOpen(false);
            return;
        }
        setOpen(false);
        navigate(ROUTES.HYPERTRACKER_CONNECTIONS);
    };

    const onWalletClick = (address: string) => {
        if (!isMemberAuthorized) {
            return;
        }
        setOpen(false);
        navigate(`${ROUTES.HYPERTRACKER_WALLET}/${address}`);
    };

    const isSelected = useMemo(() => {
        return Boolean(
            matchPath({ path: ROUTES.HYPERTRACKER_CONNECTIONS, end: true }, location.pathname),
        );
    }, [location.pathname]);

    return (
        <StyledList>
            {open && <Divider orientation={'horizontal'} sx={{ paddingTop: '4px' }} />}
            <StyledListItem disablePadding>
                <StyledLink
                    href={ROUTES.HYPERTRACKER_CONNECTIONS}
                    onClick={(e) => {
                        if (
                            e.button === 0 &&
                            !e.ctrlKey &&
                            !e.metaKey &&
                            !e.shiftKey &&
                            !e.altKey
                        ) {
                            e.preventDefault();
                            onConnectionsClick();
                        }
                    }}
                >
                    <ListItemButton
                        disableGutters
                        sx={(theme) => ({
                            minHeight: 48,
                            justifyContent: open ? 'initial' : 'center',
                            padding: '0 12px',
                            '&:hover': { backgroundColor: 'transparent' },
                            [theme.breakpoints.down('md')]: {
                                padding: 0,
                            },
                        })}
                        onClick={onConnectionsClick}
                    >
                        <ListItemButtonContent>
                            <ListItemIcon
                                sx={{
                                    minWidth: 0,
                                    justifyContent: 'center',
                                    opacity: open ? 1 : 0.5,
                                    transition: 'opacity 0.3s ease',
                                }}
                            >
                                <Badge
                                    badgeContent={totalLength || 0}
                                    invisible={!isMemberAuthorized || open || list?.length === 0}
                                    color={'info'}
                                    variant={'standard'}
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            left: 5,
                                            top: -4,
                                            fontSize: '10px',
                                        },
                                    }}
                                >
                                    <ConnectionsIcon
                                        sx={{
                                            width: '16px',
                                            height: '16px',
                                            path: {
                                                fill: (theme) =>
                                                    isSelected
                                                        ? theme.palette.hypertracker.light
                                                        : theme.palette.customText.text4,
                                            },
                                        }}
                                    />
                                </Badge>
                            </ListItemIcon>
                            <StyledItemText
                                primary={
                                    <Typography
                                        variant={'p14'}
                                        color={
                                            isSelected ? 'hypertracker.light' : 'customText.text4'
                                        }
                                        sx={{
                                            marginLeft: '12px',
                                            textTransform: 'capitalize',
                                        }}
                                    >
                                        {t('menu_connections')}
                                    </Typography>
                                }
                                sx={{ opacity: open ? 1 : 0 }}
                            />
                            {open && totalLength > 0 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '2px 8px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        borderRadius: '8px',
                                    }}
                                >
                                    <Typography variant={'p10'} color={'customText.text1'}>
                                        {totalLength}
                                    </Typography>
                                </Box>
                            )}
                        </ListItemButtonContent>
                    </ListItemButton>
                </StyledLink>
            </StyledListItem>
            {open && <Divider orientation={'horizontal'} sx={{ paddingTop: '4px' }} />}
            {open && !isMemberAuthorized && (
                <StyledListItem
                    sx={{
                        marginBottom: '12px',
                        height: 'auto',
                        minHeight: '36px',
                        alignItems: 'flex-start',
                    }}
                >
                    <Typography
                        variant={'p12'}
                        color={'secondary'}
                        component={'span'}
                        sx={{
                            cursor: 'pointer',
                            minWidth: 0,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                        onClick={onNotAuthorizedClick}
                    >
                        {t('connections_login')}
                    </Typography>
                </StyledListItem>
            )}
            {open &&
                isMemberAuthorized &&
                list?.length > 0 &&
                list.map((connection) => {
                    return (
                        <Stack
                            onClick={() => onWalletClick(connection.address)}
                            key={connection.address}
                            sx={{
                                cursor: 'pointer',
                                margin: '12px 0',
                                padding: '0 18px',
                                '&:hover': {
                                    '& span': {
                                        color: (theme) => theme.palette.secondary.main,
                                    },
                                },
                            }}
                            width={'100%'}
                            flexDirection={'row'}
                            alignItems={'center'}
                            justifyContent={'space-between'}
                        >
                            <Typography
                                variant={'p12'}
                                color={'customText.text1'}
                                component={'span'}
                            >
                                {connection.label}
                            </Typography>
                            <Typography
                                variant={'p12'}
                                color={'customText.text1'}
                                component={'span'}
                            >
                                {isNaN(connection.value)
                                    ? 'N/A'
                                    : nFormatter(connection.value, true)}
                            </Typography>
                        </Stack>
                    );
                })}
            {open && isMemberAuthorized && totalLength > 5 && (
                <Box sx={{ padding: '0 18px', marginBottom: '20px' }}>
                    <Typography color={'customText.text1'} variant={'p12'}>
                        {t('more_connections', { count: totalLength - list.length })}
                    </Typography>
                </Box>
            )}
            {open && isMemberAuthorized && totalLength === 0 && (
                <Stack
                    onClick={onAddWalletClick}
                    sx={{
                        cursor: 'pointer',
                        margin: '12px 0',
                        padding: '0 18px',
                        '&:hover': {
                            '& span:last-of-type': {
                                color: (theme) => lighten(theme.palette.hypertracker.light, 0.5),
                                borderBottom: (theme) =>
                                    `1px solid ${lighten(theme.palette.hypertracker.light, 0.5)}`,
                            },
                        },
                    }}
                    width={'100%'}
                    flexDirection={'row'}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                >
                    <Typography
                        variant={'p12'}
                        color={'customText.text1'}
                        component={'span'}
                        sx={{ borderBottom: '1px solid transparent' }}
                    >
                        {t('no_wallet')}
                    </Typography>
                    <Typography
                        variant={'p12'}
                        color={'hypertracker.light'}
                        component={'span'}
                        sx={{ borderBottom: '1px solid transparent' }}
                    >
                        {t('add_wallet')}
                    </Typography>
                </Stack>
            )}
        </StyledList>
    );
};

export default ConnectionsList;
